if (!customElements.get('sticky-atc')) {
  /**
   * De stepper in de sticky balk is een spiegel, geen tweede formulierveld:
   * het productformulier heeft al een input[name="quantity"], en twee velden
   * met dezelfde naam zouden allebei meegestuurd worden. Elke wijziging hier
   * wordt dus doorgeschreven naar dat originele veld (met een change-event,
   * zodat het quantity-input element van het thema en eventuele staffelprijzen
   * meelopen).
   *
   * Belangrijk: dit element verwijdert de stepper NOOIT. In templates/product.json
   * staat het buy_buttons-blok op "disabled": true, waardoor het koopformulier
   * (en daarmee het quantity-veld) uit een andere bron komt dan verwacht --
   * het sticky-cta paneel van het thema, met een id die op -inline eindigt.
   * Een eerdere versie verwijderde de stepper zodra hij dat veld niet kon
   * vinden, wat precies leidde tot "ik zie de aantalteller niet". Nu zoeken we
   * via meerdere routes en maken we als laatste redmiddel zelf een verborgen
   * veld aan, zodat de aantalkeuze altijd werkt.
   */
  class StickyATC extends HTMLElement {
    connectedCallback() {
      this.stepper = this.querySelector('.sticky-atc__qty');
      if (!this.stepper) return;

      this.display = this.querySelector('[data-sticky-qty-value]');
      this.source = this.findQuantityInput() || this.createQuantityInput();

      if (!this.source) return;

      this.onClick = this.onClick.bind(this);
      this.render = this.render.bind(this);

      this.stepper.addEventListener('click', this.onClick);
      this.source.addEventListener('change', this.render);
      this.render();
    }

    disconnectedCallback() {
      if (this.stepper) this.stepper.removeEventListener('click', this.onClick);
      if (this.source) this.source.removeEventListener('change', this.render);
    }

    get form() {
      const formId = this.dataset.productForm;
      const byId = formId ? document.getElementById(formId) : null;

      if (byId && byId.tagName === 'FORM') return byId;

      return document.querySelector('form[action*="/cart/add"]');
    }

    findQuantityInput() {
      const formId = this.dataset.productForm;

      // 1. Het veld staat buiten het <form> en is gekoppeld via form="".
      if (formId) {
        const linked = document.querySelector(`input[name="quantity"][form="${formId}"]`);
        if (linked) return linked;
      }

      // 2. Of het staat gewoon binnen het formulier.
      const form = this.form;
      if (form) {
        const inside = form.querySelector('input[name="quantity"]');
        if (inside) return inside;
      }

      // 3. Laatste poging: elk quantity-veld op de pagina dat niet van de
      //    winkelwagen is (die heten updates[...]) en niet van onszelf.
      const loose = [...document.querySelectorAll('input[name="quantity"]')].find(
        (input) => !this.contains(input)
      );

      return loose || null;
    }

    createQuantityInput() {
      const form = this.form;
      if (!form) return null;

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'quantity';
      input.value = '1';
      input.setAttribute('min', '1');
      input.setAttribute('step', '1');

      // In het formulier zelf hangen, niet via form="" -- dan werkt het ook
      // als het id van het formulier afwijkt van wat de sectie doorgeeft.
      form.appendChild(input);

      return input;
    }

    get min() {
      return Number(this.source.getAttribute('min')) || 1;
    }

    get max() {
      const max = this.source.getAttribute('max');
      return max ? Number(max) : Infinity;
    }

    get step() {
      return Number(this.source.getAttribute('step')) || 1;
    }

    onClick(event) {
      const button = event.target.closest('[data-sticky-qty-step]');
      if (!button || button.disabled) return;

      const direction = Number(button.dataset.stickyQtyStep);
      const current = Number(this.source.value) || this.min;
      const next = Math.min(Math.max(current + direction * this.step, this.min), this.max);

      if (next === current) return;

      this.source.value = next;
      this.source.dispatchEvent(new Event('change', { bubbles: true }));
      this.render();
    }

    render() {
      const value = Number(this.source.value) || this.min;

      if (this.display) this.display.textContent = value;

      this.stepper.querySelectorAll('[data-sticky-qty-step]').forEach((button) => {
        const direction = Number(button.dataset.stickyQtyStep);
        button.disabled = direction < 0 ? value <= this.min : value >= this.max;
      });
    }
  }

  customElements.define('sticky-atc', StickyATC);
}
